import React from 'react';
import { AlertTriangle } from 'lucide-react';
import Button from './Button';

// =============================================================================
// ConfirmDialog — generic three-action confirmation modal (Sep 2026).
//
// Styled off the existing SafetyBlockModal / AuditModal language (deep-teal
// blurred backdrop, rounded-[24px] white card). First consumer: the Publish
// auto-check ("N conflicts found — Review / Publish anyway / Cancel").
//
//   tone: 'warning' (amber chip) | 'danger' (rose chip)
//   confirmLabel/onConfirm     : primary action
//   secondaryLabel/onSecondary : optional middle action (e.g. "Review")
//   isLoading                  : true while onConfirm's action is in flight --
//     disables every button and ignores backdrop/Cancel, so a slow request
//     (e.g. a delete) can't be "canceled" from the UI while it's still
//     actually running server-side.
// =============================================================================

const ConfirmDialog = ({
  isOpen,
  onClose,
  title,
  body,
  tone = 'warning',
  confirmLabel,
  onConfirm,
  secondaryLabel,
  onSecondary,
  cancelLabel = 'Cancel',
  isLoading = false,
}) => {
  if (!isOpen) return null;

  const chip = tone === 'danger'
    ? 'bg-rose-50 text-rose-500'
    : 'bg-amber-50 text-amber-500';
  const guardedClose = isLoading ? undefined : onClose;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#042f2e]/40 backdrop-blur-sm p-4 animate-in fade-in duration-150"
      onClick={guardedClose}
    >
      <div
        className="bg-white rounded-[24px] shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-label={typeof title === 'string' ? title : undefined}
      >
        <div className="p-6">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${chip}`}>
            <AlertTriangle className="w-6 h-6" />
          </div>
          <h3 className="text-lg font-bold text-slate-800 mb-2" style={{ fontFamily: 'var(--font-header)' }}>
            {title}
          </h3>
          <div className="text-xs text-slate-500 leading-relaxed">{body}</div>
        </div>
        <div className="flex flex-col gap-2 px-6 pb-6">
          <Button variant="accent" onClick={onConfirm} isLoading={isLoading} disabled={isLoading} className="w-full justify-center font-bold text-xs">
            {confirmLabel}
          </Button>
          {secondaryLabel && (
            <Button variant="secondary" onClick={onSecondary} disabled={isLoading} className="w-full justify-center font-bold text-xs">
              {secondaryLabel}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} disabled={isLoading} className="w-full justify-center font-bold text-xs">
            {cancelLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
